import config from '../config';

/**
 * API utility functions for KPCA application
 */

/**
 * Get basic headers for API calls
 * @returns {Object} Headers object
 */
const getBasicHeaders = () => {
  return {
    'Content-Type': 'application/json'
  };
};

/**
 * Fetch complete user profile data including user_profile and company_profile
 * @param {string} userId - User's ID
 * @returns {Promise<Object>} Complete profile data
 */
export const fetchCompleteProfile = async (userId) => {
  try {
    console.log('fetching complete profile for user_id:', userId);
    console.log('API URL:', `${config.API_URL}user/profile?user_id=${encodeURIComponent(userId)}`);

    const response = await fetch(`${config.API_URL}user/profile?user_id=${encodeURIComponent(userId)}`, {
      method: 'GET',
      headers: getBasicHeaders()
    });

    const data = await response.json();
    console.log('API Response:', data);

    if (!response.ok) {
      console.error('API Error:', data);
      throw new Error(data.message || 'Failed to fetch profile data');
    }

    return data;
  } catch (error) {
    console.error('Error fetching complete profile:', error);
    throw error;
  }
};

/**
 * Update user profile data
 * @param {Object} profileData - Profile data to update
 * @returns {Promise<Object>} Update response
 */
export const updateUserProfile = async (profileData) => {
  try {
    console.log('Updating user profile:', profileData);

    const response = await fetch(`${config.API_URL}user/update-profile`, {
      method: 'POST',
      headers: getBasicHeaders(),
      body: JSON.stringify(profileData)
    });

    const data = await response.json();
    console.log('Update Profile API Response:', data);

    if (!response.ok) {
      throw new Error(data.message || 'Failed to update profile');
    }

    return data;
  } catch (error) {
    console.error('Error updating profile:', error);
    throw error;
  }
};

/**
 * Change user password
 * @param {Object} passwordData - Password change data
 * @returns {Promise<Object>} Change password response
 */
export const changePassword = async (passwordData) => {
  try {
    console.log('Changing password for user:', passwordData.userId);

    const response = await fetch(`${config.API_URL}user/change-password`, {
      method: 'POST',
      headers: getBasicHeaders(),
      body: JSON.stringify(passwordData)
    });

    const data = await response.json();
    console.log('Change Password API Response:', data);

    if (!response.ok) {
      throw new Error(data.message || 'Failed to change password');
    }

    return data;
  } catch (error) {
    console.error('Error changing password:', error);
    throw error;
  }
};

/**
 * Update company profile data
 * Note: Company name and registration number cannot be changed
 * @param {Object} companyData - Company data to update
 * @returns {Promise<Object>} Update response
 */
export const updateCompanyProfile = async (companyData) => {
  try {
    console.log('Updating company profile:', companyData);

    const response = await fetch(`${config.API_URL}user/update-company-profile`, {
      method: 'POST',
      headers: getBasicHeaders(),
      body: JSON.stringify(companyData)
    });

    const data = await response.json();
    console.log('Update Company Profile API Response:', data);

    if (!response.ok) {
      throw new Error(data.message || 'Failed to update company profile');
    }

    return data;
  } catch (error) {
    console.error('Error updating company profile:', error);
    throw error;
  }
};

/**
 * Fetch user notifications
 * @param {string} userId - User's ID
 * @param {boolean} unreadOnly - Fetch only unread notifications
 * @param {number} page - Page number (default: 1)
 * @param {number} perPage - Items per page (default: 10)
 * @returns {Promise<Object>} Notifications data with pagination info
 */
export const fetchNotifications = async (userId, unreadOnly = false, page = 1, perPage = 10) => {
  try {
    console.log('Fetching notifications for user:', userId, 'unreadOnly:', unreadOnly, 'page:', page, 'perPage:', perPage);

    let url = `${config.API_URL}notifications?user_id=${userId}&unread_only=${unreadOnly}&page=${page}&per_page=${perPage}&order_by=created_at&order_direction=desc`;

    const response = await fetch(url, {
      method: 'GET',
      headers: getBasicHeaders()
    });

    const data = await response.json();
    console.log('Fetch Notifications API Response:', data);

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch notifications');
    }

    return data;
  } catch (error) {
    console.error('Error fetching notifications:', error);
    throw error;
  }
};

/**
 * Get unread notification count
 * @param {string} userId - User's ID
 * @returns {Promise<Object>} Unread count data
 */
export const getUnreadCount = async (userId) => {
  try {
    console.log('Getting unread count for user:', userId);

    const response = await fetch(`${config.API_URL}notifications/unread-count?user_id=${userId}`, {
      method: 'GET',
      headers: getBasicHeaders()
    });

    const data = await response.json();
    console.log('Unread Count API Response:', data);

    if (!response.ok) {
      throw new Error(data.message || 'Failed to get unread count');
    }

    return data;
  } catch (error) {
    console.error('Error getting unread count:', error);
    throw error;
  }
};

/**
 * Mark notification as read
 * @param {string} notificationId - Notification ID
 * @param {string} userId - User's ID
 * @returns {Promise<Object>} Mark as read response
 */
export const markNotificationAsRead = async (notificationId, userId) => {
  try {
    console.log('Marking notification as read:', notificationId, 'for user:', userId);

    const response = await fetch(`${config.API_URL}notifications/mark-read`, {
      method: 'POST',
      headers: getBasicHeaders(),
      body: JSON.stringify({
        notification_id: notificationId,
        user_id: userId
      })
    });

    const data = await response.json();
    console.log('Mark as Read API Response:', data);

    if (!response.ok) {
      throw new Error(data.message || 'Failed to mark notification as read');
    }

    return data;
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

/**
 * Mark notification as unread
 * @param {string} notificationId - Notification ID
 * @param {string} userId - User's ID
 * @returns {Promise<Object>} Mark as unread response
 */
export const markNotificationAsUnread = async (notificationId, userId) => {
  try {
    console.log('Marking notification as unread:', notificationId, 'for user:', userId);

    const response = await fetch(`${config.API_URL}notifications/mark-unread`, {
      method: 'POST',
      headers: getBasicHeaders(),
      body: JSON.stringify({
        notification_id: notificationId,
        user_id: userId
      })
    });

    const data = await response.json();
    console.log('Mark as Unread API Response:', data);

    if (!response.ok) {
      throw new Error(data.message || 'Failed to mark notification as unread');
    }

    return data;
  } catch (error) {
    console.error('Error marking notification as unread:', error);
    throw error;
  }
};

/**
 * Mark all notifications as read
 * @param {string} userId - User's ID
 * @returns {Promise<Object>} Mark all as read response
 */
export const markAllNotificationsAsRead = async (userId) => {
  try {
    console.log('Marking all notifications as read for user:', userId);

    const response = await fetch(`${config.API_URL}notifications/mark-all-read`, {
      method: 'POST',
      headers: getBasicHeaders(),
      body: JSON.stringify({
        user_id: userId
      })
    });

    const data = await response.json();
    console.log('Mark All as Read API Response:', data);

    if (!response.ok) {
      throw new Error(data.message || 'Failed to mark all notifications as read');
    }

    return data;
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw error;
  }
};

/**
 * Mark all notifications as unread
 * @param {string} userId - User's ID
 * @returns {Promise<Object>} Mark all as unread response
 */
export const markAllNotificationsAsUnread = async (userId) => {
  try {
    console.log('Marking all notifications as unread for user:', userId);

    const response = await fetch(`${config.API_URL}notifications/mark-all-unread`, {
      method: 'POST',
      headers: getBasicHeaders(),
      body: JSON.stringify({
        user_id: userId
      })
    });

    const data = await response.json();
    console.log('Mark All as Unread API Response:', data);

    if (!response.ok) {
      throw new Error(data.message || 'Failed to mark all notifications as unread');
    }

    return data;
  } catch (error) {
    console.error('Error marking all notifications as unread:', error);
    throw error;
  }
};

/**
 * Delete a notification
 * @param {string} notificationId - Notification ID
 * @param {string} userId - User's ID
 * @returns {Promise<Object>} Delete notification response
 */
export const deleteNotification = async (notificationId, userId) => {
  try {
    console.log('Deleting notification:', notificationId, 'for user:', userId);

    const response = await fetch(`${config.API_URL}notifications/delete`, {
      method: 'POST',
      headers: getBasicHeaders(),
      body: JSON.stringify({
        notification_id: notificationId,
        user_id: userId
      })
    });

    const data = await response.json();
    console.log('Delete Notification API Response:', data);

    if (!response.ok) {
      throw new Error(data.message || 'Failed to delete notification');
    }

    return data;
  } catch (error) {
    console.error('Error deleting notification:', error);
    throw error;
  }
};

/**
 * Fetch user documents with versioning information
 * @param {string} companyId - Company ID
 * @param {string} groupBy - Group by type/date/none
 * @param {string} statusFilter - Filter by all/active/inactive
 * @param {string} documentType - Optional document type for versions
 * @returns {Promise<Object>} Documents data
 */
export const fetchUserDocuments = async (companyId, groupBy = 'type', statusFilter = 'all', documentType = null) => {
  try {
    console.log('Fetching documents for company:', companyId, 'groupBy:', groupBy, 'statusFilter:', statusFilter, 'documentType:', documentType);

    let url = `${config.API_URL}documents?company_id=${companyId}&group_by=${groupBy}&status=${statusFilter}`;

    if (documentType) {
      url += `&document_type=${documentType}`;
    }

    console.log('API URL:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: getBasicHeaders()
    });

    const data = await response.json();
    console.log('Fetch Documents API Response:', data);

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch documents');
    }

    return data;
  } catch (error) {
    console.error('Error fetching documents:', error);
    throw error;
  }
};

/**
 * Get document versions for a specific document type
 * @param {string} userId - User's ID
 * @param {string} documentType - Document type
 * @returns {Promise<Object>} Document versions data
 */
export const fetchDocumentVersions = async (userId, documentType) => {
  try {
    console.log('Fetching document versions for user:', userId, 'type:', documentType);

    const response = await fetch(`${config.API_URL}documents/versions?user_id=${userId}&document_type=${documentType}`, {
      method: 'GET',
      headers: getBasicHeaders()
    });

    const data = await response.json();
    console.log('Fetch Document Versions API Response:', data);

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch document versions');
    }

    return data;
  } catch (error) {
    console.error('Error fetching document versions:', error);
    throw error;
  }
};

/**
 * Upload a new document version
 * @param {string} userId - User's ID
 * @param {FormData} formData - Form data with document file and metadata
 * @returns {Promise<Object>} Upload response
 */
export const uploadDocument = async (userId, formData) => {
  try {
    console.log('Uploading document for user:', userId);

    // Debug FormData contents
    console.log('FormData contents before upload:');
    for (let [key, value] of formData.entries()) {
      if (key === 'document') {
        console.log('document:', {
          name: value.name,
          size: value.size,
          type: value.type,
          lastModified: value.lastModified
        });
      } else {
        console.log(`${key}:`, value);
      }
    }

    // Add user_id to form data if not already present
    if (!formData.has('user_id')) {
      formData.append('user_id', userId);
    }

    const response = await fetch(`${config.API_URL}documents/upload`, {
      method: 'POST',
      body: formData,
      headers: {
        'Accept': 'application/json',
        // Do not set Content-Type, let browser handle it for FormData
      },
      credentials: 'include', // Include cookies if any
    });

    console.log('Response status:', response.status);
    console.log('Response statusText:', response.statusText);

    // Log raw response for debugging
    console.log('Raw response:', response);
    console.log('Response headers:', Object.fromEntries([...response.headers]));

    const data = await response.json();
    console.log('Upload Document API Response:', data);

    // Check for success in multiple ways to handle different response formats
    if (response.ok && (data.status === 'success' || data.success === true || response.status === 200)) {
      console.log('✅ Document upload successful:', data);
      return data;
    } else {
      // Even if response is not OK, check if the data indicates success
      if (data.status === 'success' || data.success === true) {
        console.log('✅ Document upload successful (despite non-OK response):', data);
        return data;
      }
      throw new Error(data.message || 'Failed to upload document');
    }
  } catch (error) {
    console.error('Error uploading document:', error);
    throw error;
  }
};

/**
 * Set a document version as active
 * @param {string} userId - User's ID
 * @param {string} documentId - Document ID
 * @returns {Promise<Object>} Set active response
 */
export const setActiveDocumentVersion = async (userId, documentId) => {
  try {
    console.log('Setting active document version:', documentId, 'for user:', userId);

    const response = await fetch(`${config.API_URL}documents/set-active`, {
      method: 'POST',
      headers: getBasicHeaders(),
      body: JSON.stringify({
        document_id: documentId,
        user_id: userId
      })
    });

    const data = await response.json();
    console.log('Set Active Document API Response:', data);

    if (!response.ok) {
      throw new Error(data.message || 'Failed to set document as active');
    }

    return data;
  } catch (error) {
    console.error('Error setting active document:', error);
    throw error;
  }
};

/**
 * Download a document
 * @param {string} userId - User's ID
 * @param {string} documentId - Document ID
 * @returns {Promise<Object>} Download response with blob data
 */
export const downloadDocument = async (userId, documentId) => {
  try {
    console.log('Downloading document:', documentId, 'for user:', userId);

    const response = await fetch(`${config.API_URL}documents/download/${documentId}?user_id=${userId}`, {
      method: 'GET'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to download document');
    }

    const blob = await response.blob();
    return {
      success: true,
      data: blob
    };
  } catch (error) {
    console.error('Error downloading document:', error);
    return {
      success: false,
      message: error.message
    };
  }
};

/**
 * Search user documents
 * @param {string} userId - User's ID
 * @param {string} searchTerm - Search term
 * @returns {Promise<Object>} Search results
 */
export const searchDocuments = async (userId, searchTerm) => {
  try {
    console.log('Searching documents for user:', userId, 'term:', searchTerm);

    const response = await fetch(`${config.API_URL}documents/search?user_id=${userId}&q=${encodeURIComponent(searchTerm)}`, {
      method: 'GET',
      headers: getBasicHeaders()
    });

    const data = await response.json();
    console.log('Search Documents API Response:', data);

    if (!response.ok) {
      throw new Error(data.message || 'Failed to search documents');
    }

    return data;
  } catch (error) {
    console.error('Error searching documents:', error);
    throw error;
  }
};

/**
 * Delete a document
 * @param {string} documentId - Document ID to delete
 * @returns {Promise<Object>} Delete response
 */
export const deleteDocument = async (documentId) => {
  try {
    console.log('Deleting document:', documentId);

    const response = await fetch(`${config.API_URL}documents/delete/${documentId}`, {
      method: 'DELETE',
      headers: getBasicHeaders()
    });

    const data = await response.json();
    console.log('Delete Document API Response:', data);

    if (!response.ok) {
      throw new Error(data.message || 'Failed to delete document');
    }

    return data;
  } catch (error) {
    console.error('Error deleting document:', error);
    throw error;
  }
};

/**
 * CERTIFICATE VERIFICATION API FUNCTIONS
 */

/**
 * Verify single certificate by certificate number (for QR code verification)
 * @param {string} certificateNumber - Certificate number
 * @returns {Promise<Object>} Certificate verification data
 */
export const verifySingleCertificate = async (certificateNumber) => {
  try {
    console.log('Verifying single certificate:', certificateNumber);

    const response = await fetch(`${config.API_URL}api/verify-certificate/${encodeURIComponent(certificateNumber)}`, {
      method: 'GET',
      headers: getBasicHeaders()
    });

    const data = await response.json();
    console.log('Certificate verification response:', data);

    if (!response.ok) {
      console.error('Certificate verification error:', data);
      throw new Error(data.message || 'Failed to verify certificate');
    }

    return data;
  } catch (error) {
    console.error('Error verifying certificate:', error);
    throw error;
  }
};

/**
 * Verify certificate by certificate number, company name, or registration number
 * @param {string} searchTerm - Search term for verification
 * @returns {Promise<Object>} Verification results
 */
export const verifyCertificate = async (searchTerm) => {
  try {
    console.log('Verifying certificate with search term:', searchTerm);

    // Use the new verification endpoints with include_all parameter to show all members
    const response = await fetch(`${config.API_URL}verification/search-members?search=${encodeURIComponent(searchTerm.trim())}&include_all=true`, {
      method: 'GET',
      headers: getBasicHeaders()
    });

    const data = await response.json();
    console.log('Certificate verification response:', data);

    if (!response.ok) {
      console.error('Certificate verification error:', data);
      throw new Error(data.message || 'Failed to verify certificate');
    }

    // Transform the response to match the expected format for the UI
    const transformedData = {
      success: data.status === 'success',
      message: data.status === 'success' ?
        `${data.data?.length || 0} member(s) found` :
        (data.message || 'No members found'),
      data: []
    };

    if (data.data && Array.isArray(data.data)) {
      transformedData.data = data.data.map(member => ({
        company_info: {
          company_name: member.company_name,
          contact_person: member.contact_person,
          registration_number: member.company_registration_number,
          services_offered: [] // Could be enhanced if service data is available
        },
        certificate_info: {
          certificate_number: member.membership_number || 'Not assigned',
          certificate_type: 'KPCA Membership Certificate',
          expiry_date: member.verification_status === 'verified' ? 'Valid indefinitely' : 'Pending verification',
          days_until_expiry: null
        },
        verification_result: {
          verification_status: getVerificationStatus(member.verification_status),
          verification_message: getVerificationMessage(member.verification_status, member.verified_at),
          is_valid: member.verification_status === 'verified'
        }
      }));
    }

    return transformedData;
  } catch (error) {
    console.error('Error verifying certificate:', error);

    // Return error in expected format
    return {
      success: false,
      message: error.message || 'Failed to verify certificate',
      data: []
    };
  }
};

/**
 * Helper function to map verification status to display format
 * @param {string} status - Backend verification status
 * @returns {string} Display status
 */
function getVerificationStatus(status) {
  const statusMap = {
    'verified': 'VALID',
    'pending': 'MEMBERSHIP_INACTIVE',
    'rejected': 'REVOKED',
    'suspended': 'SUSPENDED'
  };
  return statusMap[status] || 'UNKNOWN';
}

/**
 * Helper function to generate verification message
 * @param {string} status - Verification status
 * @param {string} verifiedAt - Verification date
 * @returns {string} Verification message
 */
function getVerificationMessage(status, verifiedAt) {
  const messages = {
    'verified': `✅ This company is a verified KPCA member. Verified on ${formatVerificationDate(verifiedAt)}.`,
    'pending': '⏳ This company\'s membership application is currently under review.',
    'rejected': '❌ This company\'s membership application has been rejected.',
    'suspended': '⏸️ This company\'s membership has been suspended.'
  };
  return messages[status] || '❓ Unable to verify membership status.';
}

/**
 * Helper function to format verification date
 * @param {string} dateString - Date string
 * @returns {string} Formatted date
 */
function formatVerificationDate(dateString) {
  if (!dateString) return 'unknown date';
  try {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  } catch (error) {
    return 'unknown date';
  }
}

/**
 * Get detailed certificate information by certificate number
 * @param {string} certificateNumber - Certificate number to lookup
 * @returns {Promise<Object>} Certificate details
 */
export const getCertificateDetails = async (certificateNumber) => {
  try {
    console.log('Fetching certificate details for:', certificateNumber);

    const response = await fetch(`${config.API_URL}verify/certificate/${encodeURIComponent(certificateNumber)}`, {
      method: 'GET',
      headers: getBasicHeaders()
    });

    const data = await response.json();
    console.log('Certificate details response:', data);

    if (!response.ok) {
      console.error('Certificate details error:', data);
      throw new Error(data.message || 'Failed to fetch certificate details');
    }

    return data;
  } catch (error) {
    console.error('Error fetching certificate details:', error);
    throw error;
  }
};

/**
 * Fetch membership fees and payment types
 * @returns {Promise<Object>} Membership fees data
 */
export const fetchMembershipFees = async () => {
  try {
    console.log('Fetching membership fees');

    const response = await fetch(`${config.API_URL}membership/fees`, {
      method: 'GET',
      headers: getBasicHeaders()
    });

    const data = await response.json();
    console.log('Membership fees API response:', data);

    if (!response.ok) {
      console.error('Membership fees API error:', data);
      throw new Error(data.message || 'Failed to fetch membership fees');
    }

    return data;
  } catch (error) {
    console.error('Error fetching membership fees:', error);
    throw error;
  }
};

/**
 * MEMBER PAYMENT API FUNCTIONS
 */

/**
 * Create a new payment
 * @param {Object} paymentData - Payment data
 * @returns {Promise<Object>} Payment creation response
 */
export const createPayment = async (paymentData) => {
  try {
    console.log('Creating payment:', paymentData);

    const response = await fetch(`${config.API_URL}member/payments`, {
      method: 'POST',
      headers: getBasicHeaders(),
      body: JSON.stringify(paymentData)
    });

    const data = await response.json();
    console.log('Create payment API response:', data);

    if (!response.ok) {
      console.error('Create payment API error:', data);
      throw new Error(data.message || 'Failed to create payment');
    }

    return data;
  } catch (error) {
    console.error('Error creating payment:', error);
    throw error;
  }
};

/**
 * Get member payment history
 * @param {string} memberId - Member ID
 * @param {number} page - Page number (default: 1)
 * @param {number} limit - Items per page (default: 10)
 * @returns {Promise<Object>} Payment history data
 */
export const getMemberPaymentHistory = async (memberId, page = 1, limit = 10) => {
  try {
    console.log('Fetching payment history for member:', memberId, 'page:', page, 'limit:', limit);

    const response = await fetch(`${config.API_URL}member/payments/history?member_id=${memberId}&page=${page}&limit=${limit}`, {
      method: 'GET',
      headers: getBasicHeaders()
    });

    const data = await response.json();
    console.log('Payment history API response:', data);

    if (!response.ok) {
      console.error('Payment history API error:', data);
      throw new Error(data.message || 'Failed to fetch payment history');
    }

    return data;
  } catch (error) {
    console.error('Error fetching payment history:', error);
    throw error;
  }
};

/**
 * Get specific payment details
 * @param {string} paymentId - Payment ID
 * @returns {Promise<Object>} Payment details
 */
export const getPaymentDetails = async (paymentId) => {
  try {
    console.log('Fetching payment details for ID:', paymentId);

    const response = await fetch(`${config.API_URL}member/payments/${paymentId}`, {
      method: 'GET',
      headers: getBasicHeaders()
    });

    const data = await response.json();
    console.log('Payment details API response:', data);

    if (!response.ok) {
      console.error('Payment details API error:', data);
      throw new Error(data.message || 'Failed to fetch payment details');
    }

    return data;
  } catch (error) {
    console.error('Error fetching payment details:', error);
    throw error;
  }
};

/**
 * Update an existing payment
 * @param {string} paymentId - Payment ID
 * @param {Object} paymentData - Updated payment data
 * @returns {Promise<Object>} Update response
 */
export const updatePayment = async (paymentId, paymentData) => {
  try {
    console.log('Updating payment:', paymentId, paymentData);

    const response = await fetch(`${config.API_URL}member/payments/${paymentId}`, {
      method: 'PUT',
      headers: getBasicHeaders(),
      body: JSON.stringify(paymentData)
    });

    const data = await response.json();
    console.log('Update payment API response:', data);

    if (!response.ok) {
      console.error('Update payment API error:', data);
      throw new Error(data.message || 'Failed to update payment');
    }

    return data;
  } catch (error) {
    console.error('Error updating payment:', error);
    throw error;
  }
};

/**
 * Get certificate statistics (public endpoint)
 * @returns {Promise<Object>} Certificate statistics
 */
export const getCertificateStats = async () => {
  try {
    console.log('Fetching certificate statistics');

    const response = await fetch(`${config.API_URL}verify/stats`, {
      method: 'GET',
      headers: getBasicHeaders()
    });

    const data = await response.json();
    console.log('Certificate stats response:', data);

    if (!response.ok) {
      console.error('Certificate stats error:', data);
      throw new Error(data.message || 'Failed to fetch certificate statistics');
    }

    return data;
  } catch (error) {
    console.error('Error fetching certificate statistics:', error);
    throw error;
  }
};

/**
 * ADMIN CERTIFICATE MANAGEMENT FUNCTIONS (Require Authentication)
 */

/**
 * Issue new certificate (Admin only)
 * @param {Object} certificateData - Certificate data
 * @returns {Promise<Object>} Issued certificate
 */
export const issueCertificate = async (certificateData) => {
  try {
    console.log('Issuing new certificate:', certificateData);

    const response = await fetch(`${config.API_URL}certificates/issue`, {
      method: 'POST',
      headers: getBasicHeaders(),
      body: JSON.stringify(certificateData)
    });

    const data = await response.json();
    console.log('Issue certificate response:', data);

    if (!response.ok) {
      console.error('Issue certificate error:', data);
      throw new Error(data.message || 'Failed to issue certificate');
    }

    return data;
  } catch (error) {
    console.error('Error issuing certificate:', error);
    throw error;
  }
};

/**
 * Renew certificate (Admin only)
 * @param {Object} renewalData - Renewal data
 * @returns {Promise<Object>} Renewed certificate
 */
export const renewCertificate = async (renewalData) => {
  try {
    console.log('Renewing certificate:', renewalData);

    const response = await fetch(`${config.API_URL}certificates/renew`, {
      method: 'POST',
      headers: getBasicHeaders(),
      body: JSON.stringify(renewalData)
    });

    const data = await response.json();
    console.log('Renew certificate response:', data);

    if (!response.ok) {
      console.error('Renew certificate error:', data);
      throw new Error(data.message || 'Failed to renew certificate');
    }

    return data;
  } catch (error) {
    console.error('Error renewing certificate:', error);
    throw error;
  }
};

/**
 * Get certificates expiring soon (Admin only)
 * @param {number} days - Number of days to check ahead (default: 30)
 * @returns {Promise<Object>} Expiring certificates
 */
export const getExpiringSoon = async (days = 30) => {
  try {
    console.log('Fetching certificates expiring soon:', days);

    const response = await fetch(`${config.API_URL}certificates/expiring?days=${days}`, {
      method: 'GET',
      headers: getBasicHeaders()
    });

    const data = await response.json();
    console.log('Expiring certificates response:', data);

    if (!response.ok) {
      console.error('Expiring certificates error:', data);
      throw new Error(data.message || 'Failed to fetch expiring certificates');
    }

    return data;
  } catch (error) {
    console.error('Error fetching expiring certificates:', error);
    throw error;
  }
};

/**
 * Revoke certificate (Admin only)
 * @param {Object} revocationData - Revocation data
 * @returns {Promise<Object>} Revocation result
 */
export const revokeCertificate = async (revocationData) => {
  try {
    console.log('Revoking certificate:', revocationData);

    const response = await fetch(`${config.API_URL}certificates/revoke`, {
      method: 'POST',
      headers: getBasicHeaders(),
      body: JSON.stringify(revocationData)
    });

    const data = await response.json();
    console.log('Revoke certificate response:', data);

    if (!response.ok) {
      console.error('Revoke certificate error:', data);
      throw new Error(data.message || 'Failed to revoke certificate');
    }

    return data;
  } catch (error) {
    console.error('Error revoking certificate:', error);
    throw error;
  }
};

/**
 * Submit contact form
 * @param {Object} contactData - Contact form data
 * @returns {Promise<Object>} Submission response
 */
export const submitContactForm = async (contactData) => {
  try {
    console.log('Submitting contact form:', contactData);

    const response = await fetch(`${config.API_URL}contact/submit`, {
      method: 'POST',
      headers: getBasicHeaders(),
      body: JSON.stringify(contactData)
    });

    const data = await response.json();
    console.log('Contact form submission response:', data);

    if (!response.ok) {
      console.error('Contact form submission error:', data);
      throw new Error(data.message || 'Failed to submit contact form');
    }

    return data;
  } catch (error) {
    console.error('Error submitting contact form:', error);
    throw error;
  }
};

/**
 * Get contact messages (Admin only)
 * @param {Object} filters - Optional filters (status, subject, priority, search, page, per_page)
 * @returns {Promise<Object>} Messages and pagination data
 */
export const getContactMessages = async (filters = {}) => {
  try {
    console.log('Fetching contact messages with filters:', filters);

    // Build query parameters
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== '') {
        params.append(key, filters[key]);
      }
    });

    const queryString = params.toString();
    const url = `${config.API_URL}contact/messages${queryString ? '?' + queryString : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: getBasicHeaders()
    });

    const data = await response.json();
    console.log('Contact messages response:', data);

    if (!response.ok) {
      console.error('Get contact messages error:', data);
      throw new Error(data.message || 'Failed to fetch contact messages');
    }

    return data;
  } catch (error) {
    console.error('Error fetching contact messages:', error);
    throw error;
  }
};

/**
 * Get single contact message (Admin only)
 * @param {number} messageId - Message ID
 * @returns {Promise<Object>} Message data
 */
export const getContactMessage = async (messageId) => {
  try {
    console.log('Fetching contact message:', messageId);

    const response = await fetch(`${config.API_URL}contact/messages/${messageId}`, {
      method: 'GET',
      headers: getBasicHeaders()
    });

    const data = await response.json();
    console.log('Contact message response:', data);

    if (!response.ok) {
      console.error('Get contact message error:', data);
      throw new Error(data.message || 'Failed to fetch contact message');
    }

    return data;
  } catch (error) {
    console.error('Error fetching contact message:', error);
    throw error;
  }
};

/**
 * Update contact message (Admin only)
 * @param {number} messageId - Message ID
 * @param {Object} updateData - Data to update (status, priority)
 * @returns {Promise<Object>} Update response
 */
export const updateContactMessage = async (messageId, updateData) => {
  try {
    console.log('Updating contact message:', messageId, updateData);

    const response = await fetch(`${config.API_URL}contact/messages/${messageId}`, {
      method: 'PUT',
      headers: getBasicHeaders(),
      body: JSON.stringify(updateData)
    });

    const data = await response.json();
    console.log('Update contact message response:', data);

    if (!response.ok) {
      console.error('Update contact message error:', data);
      throw new Error(data.message || 'Failed to update contact message');
    }

    return data;
  } catch (error) {
    console.error('Error updating contact message:', error);
    throw error;
  }
};

/**
 * Get contact statistics (Admin only)
 * @returns {Promise<Object>} Contact statistics
 */
export const getContactStats = async () => {
  try {
    console.log('Fetching contact statistics');

    const response = await fetch(`${config.API_URL}contact/stats`, {
      method: 'GET',
      headers: getBasicHeaders()
    });

    const data = await response.json();
    console.log('Contact stats response:', data);

    if (!response.ok) {
      console.error('Get contact stats error:', data);
      throw new Error(data.message || 'Failed to fetch contact statistics');
    }

    return data;
  } catch (error) {
    console.error('Error fetching contact statistics:', error);
    throw error;
  }
};

export default {
  fetchCompleteProfile,
  updateUserProfile,
  changePassword,
  updateCompanyProfile,
  fetchNotifications,
  getUnreadCount,
  markNotificationAsRead,
  markNotificationAsUnread,
  markAllNotificationsAsRead,
  markAllNotificationsAsUnread,
  deleteNotification,
  fetchUserDocuments,
  fetchDocumentVersions,
  uploadDocument,
  setActiveDocumentVersion,
  downloadDocument,
  searchDocuments,
  deleteDocument,
  verifyCertificate,
  verifySingleCertificate,
  getCertificateDetails,
  getCertificateStats,
  issueCertificate,
  renewCertificate,
  getExpiringSoon,
  revokeCertificate,
  // Contact form functions
  submitContactForm,
  getContactMessages,
  getContactMessage,
  updateContactMessage,
  getContactStats,
  // Membership and payment functions
  fetchMembershipFees,
  createPayment,
  getMemberPaymentHistory,
  getPaymentDetails,
  updatePayment
}; 